import React, { useEffect, useState, useRef } from 'react';
import { FormField } from './FormField';
import { LockIcon, CreditCardIcon, MailIcon, KeyIcon, FileTextIcon, DollarSignIcon, LinkIcon, SmartphoneIcon, BellIcon, FileIcon, ChevronDownIcon, ChevronRightIcon, CreditCardIcon as SavedCardIcon, GlobeIcon, SearchIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface StandardPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSecurityTypeChange?: (securityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => void;
}
export function StandardPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSecurityTypeChange
}: StandardPaymentProps) {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [storePaymentMethod, setStorePaymentMethod] = useState(false);
  const [selectedPaymentProviders, setSelectedPaymentProviders] = useState<string[]>([]);
  const [securityOption, setSecurityOption] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [securityEmail, setSecurityEmail] = useState('');
  const [securityPhone, setSecurityPhone] = useState('');
  const [securityPassword, setSecurityPassword] = useState('');
  const [documentType, setDocumentType] = useState('government-id');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [customDocumentDescription, setCustomDocumentDescription] = useState('');
  const [securitySectionExpanded, setSecuritySectionExpanded] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  // Comprehensive list of countries with flags
  const countries = [{
    code: 'US',
    name: 'United States',
    flag: '🇺🇸'
  }, {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦'
  }, {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧'
  }, {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺'
  }, {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪'
  }, {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷'
  }, {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵'
  }, {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳'
  }, {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳'
  }, {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷'
  }, {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽'
  }, {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸'
  }, {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹'
  }, {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱'
  }, {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪'
  }, {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴'
  }, {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰'
  }, {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮'
  }, {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬'
  }, {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪'
  }, {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷'
  }, {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹'
  }, {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪'
  }, {
    code: 'CL',
    name: 'Chile',
    flag: '🇨🇱'
  }, {
    code: 'CO',
    name: 'Colombia',
    flag: '🇨🇴'
  }, {
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿'
  }, {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬'
  }, {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷'
  }, {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰'
  }, {
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺'
  }, {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩'
  }, {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪'
  }, {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱'
  }, {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷'
  }, {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾'
  }, {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬'
  }, {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿'
  }, {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭'
  }, {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱'
  }, {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹'
  }, {
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴'
  }, {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺'
  }, {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦'
  }, {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭'
  }, {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭'
  }, {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷'
  }, {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼'
  }, {
    code: 'UA',
    name: 'Ukraine',
    flag: '🇺🇦'
  }, {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳'
  }, {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const filteredCountries = countrySearchQuery ? countries.filter(country => country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) || country.code.toLowerCase().includes(countrySearchQuery.toLowerCase())) : countries;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const handleSecurityOptionChange = (newOption: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityOption(newOption);
    if (onSecurityTypeChange) {
      onSecurityTypeChange(newOption);
    }
  };
  const toggleCountrySelection = (countryCode: string) => {
    if (selectedCountries.includes(countryCode)) {
      setSelectedCountries(selectedCountries.filter(code => code !== countryCode));
    } else {
      setSelectedCountries([...selectedCountries, countryCode]);
    }
  };
  const removeSelectedCountry = (countryCode: string) => {
    setSelectedCountries(selectedCountries.filter(code => code !== countryCode));
  };
  const getCountryByCode = (code: string) => {
    return countries.find(country => country.code === code);
  };
  return <div className="space-y-6" data-id="element-2293">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2294">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2295">
          <DollarSignIcon size={18} className="mr-2 text-blue-600" data-id="element-2296" />
          Single payment
        </h2>
        <FormField label="Payment amount" required tooltip="The amount your customer will be charged" data-id="element-2297">
          <div className="relative" data-id="element-2298">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2299">
              <span className="text-gray-500" data-id="element-2300">$</span>
            </div>
            <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-2301" />
            <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-2302">
              <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-2303">
                <option data-id="element-2304">USD</option>
                <option data-id="element-2305">EUR</option>
                <option data-id="element-2306">GBP</option>
              </select>
            </div>
          </div>
        </FormField>
        <FormField label="Customer reference" tooltip="Reference for your customer" data-id="element-2307">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Reference ID" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-2308" />
        </FormField>
        <FormField label="Description" tooltip="A brief description of what this payment is for" data-id="element-2309">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Invoice #1234 or Monthly subscription" data-id="element-2310" />
        </FormField>
        <div className="mt-4" data-id="element-2311">
          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2312">
            Payment method storage
          </h3>
          <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md" data-id="element-2313">
            <div className="flex-1" data-id="element-2314">
              <div className="flex items-center" data-id="element-2315">
                <SavedCardIcon size={16} className="mr-2 text-blue-600" data-id="element-2316" />
                <span className="font-medium text-gray-800" data-id="element-2317">
                  Store payment method for future use
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-600" data-id="element-2318">
                Customer will be asked to authorize saving their payment method
                for future charges. Only payment methods that support storing
                customer details will be presented
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-2319">
                  Debit & Credit Cards
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-2320">
                  Alipay
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-2321">
                  WechatPay
                </span>
                .
              </p>
            </div>
            <label className="flex items-center cursor-pointer" data-id="element-2322">
              <input type="checkbox" className="sr-only" checked={storePaymentMethod} onChange={e => setStorePaymentMethod(e.target.checked)} data-id="element-2323" />
              <div className={`w-10 h-5 rounded-full transition-colors ${storePaymentMethod ? 'bg-blue-500' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-2324">
                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${storePaymentMethod ? 'translate-x-5' : ''}`} data-id="element-2325" />
              </div>
            </label>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2326">
        <button className="w-full text-left flex items-center justify-between text-lg font-medium text-gray-800 mb-2" onClick={() => setSecuritySectionExpanded(!securitySectionExpanded)} data-id="element-2327">
          <div className="flex items-center" data-id="element-2328">
            <LockIcon size={18} className="mr-2 text-blue-600" data-id="element-2329" />
            <span data-id="element-2330">Security settings</span>
          </div>
          {securitySectionExpanded ? <ChevronDownIcon size={20} className="text-gray-500" data-id="element-2331" /> : <ChevronRightIcon size={20} className="text-gray-500" data-id="element-2332" />}
        </button>
        {securityOption !== 'public' && !securitySectionExpanded && <div className="text-sm text-gray-600 ml-7 mt-1" data-id="element-2333">
            {securityOption === 'password' && 'Password protected'}
            {securityOption === 'email' && 'Email verification required'}
            {securityOption === 'sms' && 'SMS verification required'}
            {securityOption === 'document' && 'Document Upload Verification required'}
            {securityOption === 'geo' && `Geo-restricted to ${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'}`}
          </div>}
        {securitySectionExpanded && <div className="mb-6 mt-4" data-id="element-2334">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2335">
              Secure access method
            </h4>
            <div className="space-y-4" data-id="element-2336">
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2337">
                <label className="flex items-start cursor-pointer" data-id="element-2338">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'public'} onChange={() => handleSecurityOptionChange('public')} data-id="element-2339" />
                  <div className="ml-3" data-id="element-2340">
                    <div className="flex items-center" data-id="element-2341">
                      <LinkIcon size={16} className="mr-2 text-blue-500" data-id="element-2342" />
                      <span className="font-medium text-gray-800" data-id="element-2343">
                        Public access
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2344">
                      Everyone with the link can see and pay. No additional
                      security required. This is the simplest option for sharing
                      your payment link.
                    </p>
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2345">
                <label className="flex items-start cursor-pointer" data-id="element-2346">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'password'} onChange={() => handleSecurityOptionChange('password')} data-id="element-2347" />
                  <div className="ml-3" data-id="element-2348">
                    <div className="flex items-center" data-id="element-2349">
                      <KeyIcon size={16} className="mr-2 text-blue-500" data-id="element-2350" />
                      <span className="font-medium text-gray-800" data-id="element-2351">
                        Password protection
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2352">
                      Set a password that you'll need to share with your
                      customer directly. They'll need to enter this password to
                      access the payment page.
                    </p>
                    {securityOption === 'password' && <div className="mt-3" data-id="element-2353">
                        <input type="password" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter a secure password" value={securityPassword} onChange={e => setSecurityPassword(e.target.value)} data-id="element-2354" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2355">
                <label className="flex items-start cursor-pointer" data-id="element-2356">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'email'} onChange={() => handleSecurityOptionChange('email')} data-id="element-2357" />
                  <div className="ml-3" data-id="element-2358">
                    <div className="flex items-center" data-id="element-2359">
                      <MailIcon size={16} className="mr-2 text-blue-500" data-id="element-2360" />
                      <span className="font-medium text-gray-800" data-id="element-2361">
                        Email verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2362">
                      Restrict access to a specific email address. The customer
                      will receive a verification code via email and only they
                      will be able to access the payment page.
                    </p>
                    {securityOption === 'email' && <div className="mt-3" data-id="element-2363">
                        <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter customer's email address" value={securityEmail} onChange={e => setSecurityEmail(e.target.value)} data-id="element-2364" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2365">
                <label className="flex items-start cursor-pointer" data-id="element-2366">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'sms'} onChange={() => handleSecurityOptionChange('sms')} data-id="element-2367" />
                  <div className="ml-3" data-id="element-2368">
                    <div className="flex items-center" data-id="element-2369">
                      <SmartphoneIcon size={16} className="mr-2 text-blue-500" data-id="element-2370" />
                      <span className="font-medium text-gray-800" data-id="element-2371">
                        SMS verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2372">
                      Restrict access to a specific phone number. The customer
                      will receive a verification code via SMS and only they
                      will be able to access the payment page.
                    </p>
                    {securityOption === 'sms' && <div className="mt-3" data-id="element-2373">
                        <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter customer's phone number" value={securityPhone} onChange={e => setSecurityPhone(e.target.value)} data-id="element-2374" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2375">
                <label className="flex items-start cursor-pointer" data-id="element-2376">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'geo'} onChange={() => handleSecurityOptionChange('geo')} data-id="element-2377" />
                  <div className="ml-3 w-full" data-id="element-2378">
                    <div className="flex items-center" data-id="element-2379">
                      <GlobeIcon size={16} className="mr-2 text-blue-500" data-id="element-2380" />
                      <span className="font-medium text-gray-800" data-id="element-2381">
                        Geographic restriction
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2382">
                      Restrict access to customers physically located in
                      specific countries. Access will be determined by the
                      customer's IP address, ensuring they must be physically
                      present in one of the selected countries to access the
                      payment link.
                    </p>
                    {securityOption === 'geo' && <div className="mt-3" data-id="element-2383">
                        <div className="relative" ref={countryDropdownRef} data-id="element-2384">
                          <div className="flex flex-wrap gap-2 mb-2" data-id="element-2385">
                            {selectedCountries.map(countryCode => {
                        const country = getCountryByCode(countryCode);
                        return <div key={countryCode} className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm flex items-center" data-id="element-2386">
                                  <span className="mr-1" data-id="element-2387">{country?.flag}</span>
                                  <span data-id="element-2388">{country?.name}</span>
                                  <button onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeSelectedCountry(countryCode);
                          }} className="ml-1 text-blue-600 hover:text-blue-800" data-id="element-2389">
                                    <XIcon size={14} data-id="element-2390" />
                                  </button>
                                </div>;
                      })}
                          </div>
                          <div className="relative" data-id="element-2391">
                            <input type="text" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search for countries..." value={countrySearchQuery} onChange={e => setCountrySearchQuery(e.target.value)} onFocus={() => setShowCountryDropdown(true)} data-id="element-2392" />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2393">
                              <SearchIcon size={16} className="text-gray-400" data-id="element-2394" />
                            </div>
                          </div>
                          {showCountryDropdown && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto" data-id="element-2395">
                              {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${selectedCountries.includes(country.code) ? 'bg-blue-50' : ''}`} onClick={() => toggleCountrySelection(country.code)} data-id="element-2396">
                                    <div className="flex items-center" data-id="element-2397">
                                      <span className="mr-2" data-id="element-2398">
                                        {country.flag}
                                      </span>
                                      <span data-id="element-2399">{country.name}</span>
                                    </div>
                                    {selectedCountries.includes(country.code) && <CheckIcon size={16} className="text-blue-600" data-id="element-2400" />}
                                  </div>) : <div className="px-4 py-2 text-gray-500" data-id="element-2401">
                                  No countries found
                                </div>}
                            </div>}
                        </div>
                        <div className="flex justify-between mt-2" data-id="element-2402">
                          <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                      setShowCountryDropdown(false);
                      setCountrySearchQuery('');
                    }} data-id="element-2403">
                            Done
                          </button>
                          {selectedCountries.length > 0 && <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setSelectedCountries([])} data-id="element-2404">
                              Clear all
                            </button>}
                        </div>
                        {selectedCountries.length === 0 && <p className="mt-2 text-sm text-yellow-600 flex items-start" data-id="element-2405">
                            <BellIcon size={14} className="mr-1 mt-0.5 flex-shrink-0" data-id="element-2406" />
                            Please select at least one country to enable
                            geographic restriction.
                          </p>}
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-2407">
                <label className="flex items-start cursor-pointer" data-id="element-2408">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'document'} onChange={() => handleSecurityOptionChange('document')} data-id="element-2409" />
                  <div className="ml-3" data-id="element-2410">
                    <div className="flex items-center" data-id="element-2411">
                      <FileTextIcon size={16} className="mr-2 text-blue-500" data-id="element-2412" />
                      <span className="font-medium text-gray-800" data-id="element-2413">
                        Document Upload Verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-2414">
                      Customer will need to upload a specific document to unlock
                      the payment link. You'll be notified when they upload the
                      document and will need to review and approve it before
                      they can proceed with the payment.
                    </p>
                    {securityOption === 'document' && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-2415">
                        <div className="flex items-start mb-3 p-3 bg-blue-50 border border-blue-100 rounded-md" data-id="element-2416">
                          <BellIcon size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-2417" />
                          <p className="text-sm text-blue-700" data-id="element-2418">
                            You'll receive a notification when the customer
                            uploads a document. You must review and approve it
                            from your dashboard before the customer can access
                            the payment page.
                          </p>
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-2419">
                          Select required document type:
                        </div>
                        <div className="space-y-2" data-id="element-2420">
                          <label className="flex items-center" data-id="element-2421">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'government-id'} onChange={() => setDocumentType('government-id')} data-id="element-2422" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2423">
                              Government-issued ID (Passport, Driver's License)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-2424">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'proof-address'} onChange={() => setDocumentType('proof-address')} data-id="element-2425" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2426">
                              Proof of address (Utility bill, Bank statement)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-2427">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'business-verification'} onChange={() => setDocumentType('business-verification')} data-id="element-2428" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2429">
                              Business verification (Business license, Tax ID)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-2430">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'custom'} onChange={() => setDocumentType('custom')} data-id="element-2431" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2432">
                              Custom document
                            </span>
                          </label>
                          {documentType === 'custom' && <div className="mt-3 pl-6 space-y-3" data-id="element-2433">
                              <div data-id="element-2434">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2435">
                                  Document name
                                </label>
                                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Proof of insurance" value={customDocumentName} onChange={e => setCustomDocumentName(e.target.value)} data-id="element-2436" />
                              </div>
                              <div data-id="element-2437">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2438">
                                  Instructions for customer
                                </label>
                                <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Explain what document you need and any specific requirements" rows={3} value={customDocumentDescription} onChange={e => setCustomDocumentDescription(e.target.value)} data-id="element-2439" />
                              </div>
                              <div className="flex items-center text-xs text-gray-500" data-id="element-2440">
                                <FileIcon size={12} className="mr-1 text-gray-400" data-id="element-2441" />
                                These instructions will be shown to the customer
                                on the payment page
                              </div>
                            </div>}
                        </div>
                      </div>}
                  </div>
                </label>
              </div>
            </div>
          </div>}
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} selectedPaymentProviders={selectedPaymentProviders} onSelectedPaymentProvidersChange={setSelectedPaymentProviders} data-id="element-2442" />
    </div>;
}